import { format } from "date-fns";

import { ResultHl7Preview } from "features/results/components/ResultHl7Preview/ResultHl7Preview";
import { useRadiologyHL7OrderDetails } from "features/results/hooks/useRadiologyHL7OrderDetails";
import { useResultHl7Document } from "features/results/hooks/useResultHl7Document";
import { getResultReviewStatus } from "features/results/utils/resultReview";

import { XmlXsltPreview } from "../XmlXsltPreview/XmlXsltPreview";
import { RadiologyHL7DetailsTab } from "./RadiologyHL7DetailsTab";
import styles from "./RadiologyResultDetails.module.css";

export const parseResultValue = (value) => {
  if (!value) return null;
  return value.split(/\\\.br\\/g).filter((line) => line.trim());
};

export const formatResultDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const datePart = dateString.split(" ")[0].replace(/\//g, "-");
    return format(new Date(datePart), "dd-MM-yyyy");
  } catch {
    return "-";
  }
};

export const ResultCard = ({ result, index }) => {
  const parsedValue = parseResultValue(result.value);
  const resultStatus = result.resultStatus;

  return (
    <div key={index} className={styles.hl7Section}>
      <h3 className={styles.hl7SectionTitle}>
        {result.testName || "Unknown Test"}
      </h3>
      <div className={styles.hl7Grid}>
        <div className={styles.hl7GridItem}>
          <label>Date:</label>
          <span>{formatResultDate(result.date)}</span>
        </div>
        {resultStatus && (
          <div className={styles.hl7GridItem}>
            <label>Result Status:</label>
            <span>{resultStatus === "F" ? "Final" : resultStatus}</span>
          </div>
        )}
        {result.units && (
          <div className={styles.hl7GridItem}>
            <label>Units:</label>
            <span>{result.units}</span>
          </div>
        )}
        {(result.lowerLimit || result.upperLimit) && (
          <div className={styles.hl7GridItem}>
            <label>Reference Range:</label>
            <span>
              {result.lowerLimit || "?"} - {result.upperLimit || "?"}
            </span>
          </div>
        )}
      </div>
      {parsedValue && parsedValue.length > 0 && (
        <div className={styles.resultContentSection}>
          <div className={styles.notesContainer}>
            {parsedValue.map((line, idx) => (
              <p key={idx} className={styles.noteLine}>
                {line}
              </p>
            ))}
          </div>
        </div>
      )}
      {!parsedValue && (
        <p className={styles.noObservations}>No detailed result available</p>
      )}
    </div>
  );
};

export const RadiologyResultDetails = ({ item }) => {
  /*
   * The message document for this row: the list's own `rawXml` while the list still
   * sends it, otherwise fetched by control id when the row is opened (KIN-2120). Either
   * way, a row with a document of its own skips the structured detail fetch below.
   */
  const hl7Document = useResultHl7Document(item);

  const { data: hl7Details, isLoading: isHl7Loading } =
    useRadiologyHL7OrderDetails(item.id, {
      enabled: !hl7Document.hasPreview && !hl7Document.isLoading,
    });

  const patientName = item.patient
    ? `${item.patient.firstName} ${item.patient.surname}`
    : item.patientInformationExternal?.fullName || "Unknown Patient";

  const doctorName = item.patient?.doctorDetails
    ? `${item.patient.doctorDetails.firstName} ${item.patient.doctorDetails.lastName}`
    : "-";

  const testNames = item.testNamesList?.filter(Boolean).join(", ") || "-";

  const reviewStatus = getResultReviewStatus(item);

  return (
    <>
      {/* Parsed results first. Skip the whole block when there are no parsed
          results — the HL7 preview below carries the message. */}
      {!!item.results?.length && (
        <div className={styles.hl7Container}>
          <div className={styles.hl7Section}>
            <h3 className={styles.hl7SectionTitle}>Order Details</h3>
            <div className={styles.hl7Grid}>
              <div className={styles.hl7GridItem}>
                <label>Patient:</label>
                <span>{patientName}</span>
              </div>
              <div className={styles.hl7GridItem}>
                <label>Referring Doctor:</label>
                <span>{doctorName}</span>
              </div>
              <div className={styles.hl7GridItem}>
                <label>Status:</label>
                <span>{reviewStatus.label}</span>
              </div>
              <div className={styles.hl7GridItem}>
                <label>Tests:</label>
                <span>{testNames}</span>
              </div>
            </div>
          </div>

          {item.results.map((result, index) => (
            <ResultCard key={result.id || index} result={result} index={index} />
          ))}
        </div>
      )}

      {/* HL7 document below the results. */}
      <div className={styles.hl7PreviewSection}>
        <ResultHl7Preview
          document={hl7Document}
          documentKind={item.resultType}
          fallback={
            <XmlXsltPreview
              messageId={item.messageId}
              messageType="radiology"
              fallback={
                <RadiologyHL7DetailsTab
                  loading={isHl7Loading}
                  hl7Details={hl7Details}
                />
              }
            />
          }
        />
      </div>
    </>
  );
};
